import {
  formatColorPickerColorHex,
  TokenSearchInput,
  type SearchOption,
  type SearchToken,
} from "@carbon6/c6-frontend-design";
import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { labelExplanation } from "./labelExplanation";
import { deriveLabelSuggestions, filterLabelSuggestions } from "./labelSuggestions";
import { useLabelCatalog } from "./useLabelCatalog";
import styles from "./OpenLabelMultiInput.module.css";

// 메뉴에 한 번에 표시하는 제안 상한. c6 기본값과 같은 수를 쓴다.
const MAX_SUGGESTIONS = 8;

// 값을 추가한다(순수 함수). 공백을 trim 한다. 빈값이나 중복이면 같은 참조를 반환하고, 아니면 끝에 더한다.
export function addLabelValue(selected: readonly string[], raw: string): readonly string[] {
  const value = raw.trim();
  if (value === "" || selected.includes(value)) {
    return selected;
  }
  return [...selected, value];
}

// 값을 제거한다(순수 함수).
export function removeLabelValue(selected: readonly string[], value: string): readonly string[] {
  return selected.filter((entry) => entry !== value);
}

export type OpenLabelMultiInputProps = {
  // 제안 계산과 토큰 색에 쓰는 라벨 key(예: "task").
  labelKey: string;
  // 현재 선택된 값들(토큰으로 표시).
  selected: readonly string[];
  busy: boolean;
  placeholder?: string;
  // 선택 변경(추가/제거). 즉시 커밋하는 상위 컴포넌트가 서버에 반영한다.
  onChange: (values: readonly string[]) => void;
};

/**
 * open 정책 라벨(task 등)의 다중값 입력. c6 `TokenSearchInput`(검색 입력 + 토큰) 한 컨트롤이다.
 *
 * 고른 값은 입력 안쪽에 토큰으로 남는다. 입력하면 메뉴가 같은 key 의 기존 라벨로 좁혀진다.
 * open 정책이라 카탈로그에 없는 값도 새로 넣을 수 있어야 한다. 하지만 `TokenSearchInput` 은
 * 옵션에 있는 값만 토큰으로 만든다. 그래서 자유 입력 경로를 두 개 추가한다:
 * - 후보가 0개일 때 메뉴에 표시하는 `「입력어」 추가` 행(c6 가 `renderOnEmpty` 로 제공하는 자리)
 * - 같은 조건에서의 Enter
 *
 * Enter 는 후보가 0개일 때만 처리한다. 후보가 있으면 c6 가 Enter 를 처리한다(↑↓ 로 고른 후보를
 * 넣는다). 전달하는 options 는 이미 같은 질의로 좁혀져 있다. 그래서 c6 가 다시 거른 목록은 이
 * 목록의 부분집합이다. 이 목록이 비면 c6 목록도 비어서 강조할 후보가 없다.
 *
 * 제안 카탈로그는 마운트할 때 한 번 적재한다. 적재가 실패해도 자유 입력은 그대로 동작한다.
 */
export function OpenLabelMultiInput({
  labelKey,
  selected,
  busy,
  placeholder,
  onChange,
}: OpenLabelMultiInputProps): ReactNode {
  const [draft, setDraft] = useState("");
  // 제안은 보조 기능이다. 적재가 실패해도 빈 카탈로그로 계속한다. 자유 입력은 그대로 동작한다.
  const { catalog } = useLabelCatalog();

  const suggestions = useMemo(() => deriveLabelSuggestions(catalog, labelKey), [catalog, labelKey]);
  const colorByValue = useMemo(
    () => new Map(suggestions.map((suggestion) => [suggestion.value, suggestion.color])),
    [suggestions],
  );

  const selectedSet = new Set(selected);
  const options: SearchOption[] = filterLabelSuggestions(suggestions, draft, MAX_SUGGESTIONS)
    .filter((suggestion) => !selectedSet.has(suggestion.value))
    .map((suggestion) => ({
      value: suggestion.value,
      label: suggestion.value,
      // 값 이름만으로는 무엇을 고르는지 알기 어려우므로 설명을 보조 줄로 함께 표시한다.
      description:
        labelExplanation({
          key: labelKey,
          value: suggestion.value,
          description: suggestion.description,
        }) ?? undefined,
      accentColor: formatColorPickerColorHex(suggestion.color),
    }));

  const tokens: SearchToken[] = selected.map((value) => ({
    value,
    label: value,
    accentColor: formatColorPickerColorHex(colorByValue.get(value) ?? 0),
  }));

  function addFreeValue(raw: string): void {
    const next = addLabelValue(selected, raw);
    setDraft("");
    if (next !== selected) {
      onChange(next);
    }
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) {
      return;
    }
    // 고를 후보가 남아 있으면 c6 가 Enter 를 처리한다(↑↓ 로 고른 후보를 넣는다).
    if (options.length > 0 || draft.trim() === "") {
      return;
    }
    event.preventDefault();
    addFreeValue(draft);
  }

  return (
    <TokenSearchInput
      inputSize="sm"
      width="100%"
      inputValue={draft}
      selectedTokens={tokens}
      options={options}
      placeholder={placeholder ?? "값을 입력해 주세요."}
      aria-label={`${labelKey} 값`}
      disabled={busy}
      onInputValueChange={setDraft}
      onSelectedTokensChange={(next) => onChange(next.map((token) => token.value))}
      onKeyDown={handleKeyDown}
      renderOnEmpty={(query) =>
        query.trim() === "" ? (
          <span className={styles.emptyHint}>값을 입력하면 기존 라벨을 찾아 줍니다.</span>
        ) : (
          <button
            type="button"
            className={styles.addOption}
            // mousedown 기본 동작(입력 blur)을 막아야 메뉴가 닫히기 전에 클릭이 도달한다.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => addFreeValue(query)}
          >
            {`「${query.trim()}」 추가`}
          </button>
        )
      }
    />
  );
}
